import { useNavigate, useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { FileText, Clock, UsersThree, Buildings, EnvelopeSimple, ClockClockwise, EnvelopeOpen, UserCircle, ShieldCheck, List, SignOut, Briefcase, Shield, DownloadSimple, ChatCircleDots, Signature, ShareNetwork, Factory, ClipboardText, Kanban, Storefront, Wrench, Armchair, Files } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { canManageEmployees, canManageEmail, canManageUsers, canViewAuditLog, isAdmin, canUseApp } from '@/auth/AuthContext';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useT } from '@/i18n/LanguageContext';
import { ROLE_DOT, userInitial, userRoleKey } from './headerConstants';
import LanguageToggle from './LanguageToggle';

function MobileNavItem({ label, Icon, active, onClick, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
        active ? 'bg-teal-700 text-white' : 'text-zinc-700 hover:bg-zinc-100'
      }`}
    >
      <Icon size={18} weight="duotone" className={active ? 'text-white' : 'text-teal-700'} />
      <span className="truncate flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid="mobile-nav-unverified-badge"
          className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
            active ? 'bg-white text-red-600' : 'bg-red-100 text-red-600'
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/** Right-side hamburger Sheet — full mobile navigation including settings sub-items. */
export default function MobileNav({ user, showSettings, unverifiedCount = 0, open, onOpenChange, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const { canInstall, installed, isIOS, promptInstall } = useInstallPrompt();
  const isActive = (path) => location.pathname.startsWith(path);
  const initial = userInitial(user);
  const roleKey = userRoleKey(user);
  const showInstall = !installed && (canInstall || isIOS);

  const handleInstall = async () => {
    onOpenChange(false);
    if (canInstall) {
      const ok = await promptInstall();
      if (ok) toast.success(t('account.installedAddedToast'));
    } else if (isIOS) {
      toast.info(t('account.installIosHint'));
    }
  };

  const go = (path) => {
    navigate(path);
    onOpenChange(false);
  };

  return (
    <div className="md:hidden">
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label={t('nav.openMenu')}
            className="inline-flex items-center justify-center w-10 h-10 rounded-md text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <List size={22} weight="bold" />
          </button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[280px] p-0 flex flex-col" data-testid="mobile-nav-sheet">
          <SheetTitle className="sr-only">{t('nav.menu')}</SheetTitle>
          <div className="px-5 py-4 border-b border-zinc-200">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-400">{t('nav.apps')}</p>
              <LanguageToggle />
            </div>
            <p className="text-base font-bold text-zinc-900 mt-0.5 flex items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-zinc-100 text-zinc-700">
                <Briefcase size={11} weight="fill" />
              </span>
              Sensbey Web App
            </p>
            {user && typeof user === 'object' && (
              <div className="mt-3 flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-zinc-100 text-zinc-700 text-sm font-bold shadow-sm shrink-0">
                  {initial}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 truncate leading-tight">{user.name}</p>
                  <p className="text-[10px] text-zinc-500 truncate leading-tight">{user.email}</p>
                  <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-100 text-[9px] font-semibold text-zinc-600 uppercase tracking-wider">
                    <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[roleKey]}`} />
                    {t(`role.${roleKey}`)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {canUseApp(user, 'kintai') && (
              <>
                <MobileNavItem label={t('nav.forms')} Icon={FileText} active={isActive('/forms')} onClick={() => go('/forms')} testid="mobile-nav-dashboard" />
                <MobileNavItem label={t('nav.history')} Icon={Clock} active={isActive('/history') || location.pathname.startsWith('/submission')} onClick={() => go('/history')} testid="mobile-nav-history" badge={unverifiedCount} />
              </>
            )}
            {canUseApp(user, 'it') && (
              <MobileNavItem label={t('nav.it')} Icon={ShareNetwork} active={isActive('/it')} onClick={() => go('/it')} testid="mobile-nav-it" />
            )}
            {canUseApp(user, 'mfg') && (
              <MobileNavItem label={t('nav.mfg')} Icon={Factory} active={isActive('/mfg')} onClick={() => go('/mfg')} testid="mobile-nav-mfg" />
            )}
            {canUseApp(user, 'shizai') && (
              <MobileNavItem label={t('nav.shizai')} Icon={ClipboardText} active={isActive('/shizai')} onClick={() => go('/shizai')} testid="mobile-nav-shizai" />
            )}
            {canUseApp(user, 'marketing') && (
              <MobileNavItem label={t('nav.marketing')} Icon={Storefront} active={isActive('/marketing')} onClick={() => go('/marketing')} testid="mobile-nav-marketing" />
            )}
            {canUseApp(user, 'planner') && (
              <MobileNavItem label={t('nav.planner')} Icon={Kanban} active={isActive('/planner')} onClick={() => go('/planner')} testid="mobile-nav-planner" />
            )}
            {canUseApp(user, 'engineering') && (
              <MobileNavItem label={t('nav.engineering')} Icon={Wrench} active={isActive('/engineering')} onClick={() => go('/engineering')} testid="mobile-nav-engineering" />
            )}
            {canUseApp(user, 'bihin') && (
              <MobileNavItem label={t('nav.bihin')} Icon={Armchair} active={isActive('/bihin')} onClick={() => go('/bihin')} testid="mobile-nav-bihin" />
            )}
            {canUseApp(user, 'chat') && (
              <MobileNavItem label={t('nav.chat')} Icon={ChatCircleDots} active={isActive('/chat')} onClick={() => go('/chat')} testid="mobile-nav-chat" />
            )}
            {canUseApp(user, 'docs') && (
              <MobileNavItem label={t('app.docs')} Icon={Files} active={isActive('/documents')} onClick={() => go('/documents')} testid="mobile-nav-docs" />
            )}
            {showSettings && (
              <>
                <div className="pt-3 mt-3 border-t border-zinc-100">
                  <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-1">{t('settings.section')}</p>
                </div>
                {canManageEmployees(user) && (
                  <MobileNavItem label={t('settings.employees')} Icon={UsersThree} active={isActive('/settings/employees')} onClick={() => go('/settings/employees')} testid="mobile-nav-employees" />
                )}
                {canManageUsers(user) && (
                  <MobileNavItem label={t('settings.users')} Icon={UserCircle} active={isActive('/settings/users')} onClick={() => go('/settings/users')} testid="mobile-nav-users" />
                )}
                {isAdmin(user) && (
                  <MobileNavItem label={t('settings.roles')} Icon={Shield} active={isActive('/settings/roles')} onClick={() => go('/settings/roles')} testid="mobile-nav-roles" />
                )}
                <MobileNavItem label={t('settings.departments')} Icon={Buildings} active={isActive('/settings/departments')} onClick={() => go('/settings/departments')} testid="mobile-nav-departments" />
                <MobileNavItem label={t('settings.signature')} Icon={Signature} active={isActive('/settings/signature')} onClick={() => go('/settings/signature')} testid="mobile-nav-signature" />
                {canViewAuditLog(user) && (
                  <MobileNavItem label={t('settings.auditLog')} Icon={ShieldCheck} active={isActive('/settings/audit-log')} onClick={() => go('/settings/audit-log')} testid="mobile-nav-audit" />
                )}
                {canManageEmail(user) && (
                  <>
                    <MobileNavItem label={t('settings.email')} Icon={EnvelopeSimple} active={isActive('/settings/email') && !isActive('/settings/email-templates') && !isActive('/settings/email-log')} onClick={() => go('/settings/email')} testid="mobile-nav-email" />
                    <MobileNavItem label={t('settings.emailTemplates')} Icon={EnvelopeOpen} active={isActive('/settings/email-templates')} onClick={() => go('/settings/email-templates')} testid="mobile-nav-email-templates" />
                    <MobileNavItem label={t('settings.emailLog')} Icon={ClockClockwise} active={isActive('/settings/email-log')} onClick={() => go('/settings/email-log')} testid="mobile-nav-email-log" />
                  </>
                )}
              </>
            )}
          </nav>
          <div className="p-3 border-t border-zinc-200 space-y-1.5">
            {showInstall && (
              <button type="button" onClick={handleInstall} data-testid="mobile-nav-install-app" className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-teal-700 hover:bg-blue-50 transition-colors">
                <DownloadSimple size={16} weight="duotone" />
                {t('common.installApp')}
              </button>
            )}
            <button type="button" onClick={async () => { onOpenChange(false); await onLogout(); }} data-testid="mobile-nav-logout" className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
              <SignOut size={16} weight="duotone" />
              {t('common.logout')}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
